import type {
  Post,
  Store,
  Topic,
  Location,
  Page,
  SEOMeta,
  AuthorProfile,
  TagInfo,
} from "@/types/content";
import {
  canonical,
  articleSchema,
  collectionPageSchema,
  webPageSchema,
  localBusinessSchema,
  profilePageSchema,
  organizationSchema,
  websiteSchema,
  graphSchema,
} from "@/utils/seo";

/* ---------- Post ---------- */

export function buildPostSEO(post: Post): SEOMeta {
  const defaultTitle = `${post.title}｜美好時光指南`;
  const defaultDesc = post.excerpt;
  const defaultKeywords = [
    ...post.tags,
    post.category,
    "探店",
    "評比",
    "美好時光指南",
  ];

  return {
    title: post.seo?.title || defaultTitle,
    description: post.seo?.description || defaultDesc,
    ogImage: post.seo?.ogImage || post.coverImage,
    canonical: post.seo?.canonical || canonical(`/articles/${post.slug}`),
    keywords: post.seo?.keywords || defaultKeywords,
    ogType: "article",
    noindex: post.seo?.noindex,
    structuredData:
      post.seo?.structuredData ||
      articleSchema({
        title: post.title,
        description: post.excerpt,
        path: `/articles/${post.slug}`,
        image: post.coverImage,
        publishedAt: post.publishedAt,
        authorName: post.author?.name,
        category: post.category,
        tags: post.tags,
      }),
  };
}

/* ---------- Store ---------- */

export function buildStoreSEO(store: Store): SEOMeta {
  const defaultTitle = `${store.name}｜美好時光指南探店`;
  const defaultDesc = store.description;
  const defaultKeywords = [
    store.category,
    ...store.tags,
    "探店",
    "評比",
    "美好時光指南",
  ];

  return {
    title: store.seo?.title || defaultTitle,
    description: store.seo?.description || defaultDesc,
    ogImage: store.seo?.ogImage || store.coverImage,
    canonical: store.seo?.canonical || canonical(`/stores/${store.slug}`),
    keywords: store.seo?.keywords || defaultKeywords,
    ogType: "website",
    noindex: store.seo?.noindex,
    structuredData:
      store.seo?.structuredData ||
      localBusinessSchema(
        store.name,
        store.description,
        `/stores/${store.slug}`,
        store.coverImage,
        store.rating,
        store.reviewCount,
        store.location,
      ),
  };
}

/* ---------- Topic ---------- */

export function buildTopicSEO(topic: Topic): SEOMeta {
  const defaultTitle = `${topic.title}｜美好時光指南主題`;
  const defaultDesc = topic.description;

  return {
    title: topic.seo?.title || defaultTitle,
    description: topic.seo?.description || defaultDesc,
    ogImage: topic.seo?.ogImage || topic.coverImage,
    canonical: topic.seo?.canonical || canonical(`/topics/${topic.slug}`),
    keywords: topic.seo?.keywords || [topic.title, "主題探索", "美好時光指南"],
    ogType: "website",
    noindex: topic.seo?.noindex,
    structuredData:
      topic.seo?.structuredData ||
      collectionPageSchema(
        defaultTitle,
        defaultDesc,
        `/topics/${topic.slug}`,
      ),
  };
}

/* ---------- Location ---------- */

export function buildLocationSEO(location: Location): SEOMeta {
  const defaultTitle = `${location.name}｜美好時光指南城市指南`;
  const defaultDesc = location.description;

  return {
    title: location.seo?.title || defaultTitle,
    description: location.seo?.description || defaultDesc,
    ogImage: location.seo?.ogImage || location.coverImage,
    canonical:
      location.seo?.canonical || canonical(`/locations/${location.slug}`),
    keywords:
      location.seo?.keywords || [location.name, "城市指南", "美好時光指南"],
    ogType: "website",
    noindex: location.seo?.noindex,
    structuredData:
      location.seo?.structuredData ||
      collectionPageSchema(
        defaultTitle,
        defaultDesc,
        `/locations/${location.slug}`,
      ),
  };
}

/* ---------- Author ---------- */

export function buildAuthorSEO(author: AuthorProfile): SEOMeta {
  const defaultTitle = `${author.name}｜美好時光指南作者`;
  const defaultDesc = `${author.bio} 已發表 ${author.articleCount} 篇評測文章，瀏覽 ${author.name} 的所有作品。`;

  return {
    title: author.seo?.title || defaultTitle,
    description: author.seo?.description || defaultDesc,
    ogImage: author.seo?.ogImage || author.avatar,
    canonical: author.seo?.canonical || canonical(`/authors/${author.slug}`),
    keywords:
      author.seo?.keywords || [author.name, "作者", "評測", "美好時光指南"],
    ogType: "profile",
    noindex: author.seo?.noindex,
    structuredData:
      author.seo?.structuredData ||
      profilePageSchema(
        author.name,
        author.bio,
        author.avatar,
        `/authors/${author.slug}`,
      ),
  };
}

/* ---------- Tag ---------- */

export function buildTagSEO(tag: TagInfo): SEOMeta {
  const defaultTitle = `${tag.name}｜美好時光指南標籤`;
  const defaultDesc = `瀏覽「${tag.name}」相關的文章與探店店家。`;

  return {
    title: tag.seo?.title || defaultTitle,
    description: tag.seo?.description || defaultDesc,
    ogImage: tag.seo?.ogImage,
    canonical: tag.seo?.canonical || canonical(`/tags/${tag.slug}`),
    keywords:
      tag.seo?.keywords || [tag.name, "標籤", "探店", "美好時光指南"],
    ogType: "website",
    noindex: tag.seo?.noindex,
    structuredData:
      tag.seo?.structuredData ||
      collectionPageSchema(
        defaultTitle,
        defaultDesc,
        `/tags/${tag.slug}`,
      ),
  };
}

/* ---------- Static Page (about, faq, privacy, terms, partners, join-us) ---------- */

export function buildPageSEO(
  page: Page,
  fallbackDescription: string,
  fallbackKeywords: string[],
): SEOMeta {
  const defaultTitle = `${page.title}｜美好時光指南`;

  return {
    title: page.seo?.title || defaultTitle,
    description: page.seo?.description || fallbackDescription,
    ogImage: page.seo?.ogImage,
    canonical: page.seo?.canonical || canonical(`/${page.slug}`),
    keywords: page.seo?.keywords || fallbackKeywords,
    ogType: "website",
    noindex: page.seo?.noindex,
    structuredData:
      page.seo?.structuredData ||
      webPageSchema(
        defaultTitle,
        page.seo?.description || fallbackDescription,
        `/${page.slug}`,
      ),
  };
}

/* ---------- Home ---------- */

export function buildHomeSEO(): SEOMeta {
  const title =
    "美好時光指南 GoldenTimeGuides - 美業探店懶人包與真實評比";
  const description =
    "美好時光指南透過神秘客實地走訪，為你評比全台最優質的美業店家，從按摩、SPA、美甲到美髮，幫你找到不踩雷的美好體驗。";

  return {
    title,
    description,
    canonical: canonical("/"),
    keywords: [
      "美業探店",
      "SPA評比",
      "按摩推薦",
      "美甲懶人包",
      "美好時光指南",
      "GoldenTimeGuides",
    ],
    ogType: "website",
    structuredData: graphSchema([
      websiteSchema(),
      organizationSchema(
        "https://readdy.ai/api/search-image?query=A%20bold%20geometric%20compass%20needle%20logo%20icon%20for%20a%20beauty%20discovery%20guide%20brand%20GoldenTimeGuides%2C%20featuring%20a%20sharp%20symmetrical%20directional%20pointer%20pointing%20northeast%20inside%20a%20perfect%20circular%20ring%2C%20with%20a%20small%20decorative%20star%20at%20the%20center%20pivot%20point%2C%20rendered%20in%20vivid%20burnt%20orange%20and%20warm%20ivory%20white%2C%20ultra-clean%20flat%20vector%20graphic%20with%20thick%20confident%20lines%2C%20absolutely%20no%20clock%20elements%2C%20no%20text%2C%20centered%20on%20a%20solid%20pure%20white%20background%2C%20modern%20app%20icon%20style%2C%20crisp%20edges%20and%20minimalist%20design%20for%20website%20branding&width=1200&height=1200&seq=gtg-logo-schema-v1&orientation=squarish",
      ),
    ]),
  };
}

/* ---------- Articles List ---------- */

export function buildArticlesListSEO(): SEOMeta {
  const title = "最新文章｜美好時光指南";
  const description =
    "神秘客實測、懶人包整理、完整評比，幫你找到最棒的體驗與最優質的店家。";

  return {
    title,
    description,
    canonical: canonical("/articles"),
    keywords: ["最新文章", "美業探店", "懶人包", "評比", "美好時光指南"],
    ogType: "website",
    structuredData: collectionPageSchema(title, description, "/articles"),
  };
}

/* ---------- Stores List ---------- */

export function buildStoresListSEO(): SEOMeta {
  const title = "探店精選｜美好時光指南";
  const description =
    "神秘客實地走訪，為你帶來最真實的店家評比與體驗心得，從按摩、SPA、美甲到美髮，幫你找到不踩雷的好店家。";

  return {
    title,
    description,
    canonical: canonical("/stores"),
    keywords: [
      "探店精選",
      "美業店家",
      "按摩推薦",
      "SPA評比",
      "美甲",
      "美好時光指南",
    ],
    ogType: "website",
    structuredData: collectionPageSchema(title, description, "/stores"),
  };
}

/* ---------- Topics List ---------- */

export function buildTopicsListSEO(): SEOMeta {
  const title = "探索主題｜美好時光指南";
  const description =
    "從情侶約會到運動健身，找到你感興趣的主題，發現更多好活動與優質店家。";

  return {
    title,
    description,
    canonical: canonical("/topics"),
    keywords: ["主題探索", "活動主題", "美業主題", "美好時光指南"],
    ogType: "website",
    structuredData: collectionPageSchema(title, description, "/topics"),
  };
}

/* ---------- Locations List ---------- */

export function buildLocationsListSEO(): SEOMeta {
  const title = "探索城市｜美好時光指南";
  const description =
    "從台北到高雄，發現每座城市最棒的活動與體驗，瀏覽各城市精選店家與文章。";

  return {
    title,
    description,
    canonical: canonical("/locations"),
    keywords: [
      "城市探索",
      "台北活動",
      "高雄活動",
      "台中活動",
      "美好時光指南",
    ],
    ogType: "website",
    structuredData: collectionPageSchema(title, description, "/locations"),
  };
}

/* ---------- Authors List ---------- */

export function buildAuthorsListSEO(): SEOMeta {
  const title = "作者專區｜美好時光指南";
  const description =
    "認識美好時光指南的神秘客與編輯團隊，瀏覽他們的專業評測文章。";

  return {
    title,
    description,
    canonical: canonical("/authors"),
    keywords: ["作者專區", "神秘客", "編輯團隊", "評測文章", "美好時光指南"],
    ogType: "website",
    structuredData: collectionPageSchema(title, description, "/authors"),
  };
}

/* ---------- Tags List ---------- */

export function buildTagsListSEO(): SEOMeta {
  const title = "標籤專區｜美好時光指南";
  const description =
    "瀏覽美好時光指南的所有標籤，找到你感興趣的文章與探店店家。";

  return {
    title,
    description,
    canonical: canonical("/tags"),
    keywords: ["標籤專區", "美業標籤", "文章標籤", "探店標籤", "美好時光指南"],
    ogType: "website",
    structuredData: collectionPageSchema(title, description, "/tags"),
  };
}

/* ---------- Breadcrumb ---------- */

export interface BreadcrumbItem {
  name: string;
  path: string;
}

export function breadcrumbSchema(items: BreadcrumbItem[]): Record<string, unknown> {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: canonical(item.path),
    })),
  };
}